import PropTypes from "prop-types";
import React from "react";

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
// import MDInput from "components/MDInput";
function ProjectDetailsModal({ open, onClose, project }) {
  const [tabValue, setTabValue] = React.useState(0);
  const [revealed, setRevealed] = React.useState({});
  if (!project) return null;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const isSensitiveKey = (key) => /aadhaar|aadhar|account|acct|bank\s*account/i.test(key);

  const formatValue = (key, value) => {
    if (value === null || value === undefined || value === "") return "Not provided yet";

    // Date-like keys
    if (/date|dob|birth/i.test(key)) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        try {
          return new Intl.DateTimeFormat("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }).format(d);
        } catch (_) {
          return d.toLocaleDateString();
        }
      }
    }

    // Currency-like keys
    if (/income|fees|fee|amount|rupee|rs\.?/i.test(key)) {
      const num = Number(value);
      if (!isNaN(num)) {
        return new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          maximumFractionDigits: 0,
        }).format(num);
      }
    }

    // Phone normalize
    if (/phone|mobile|contact/i.test(key)) {
      const str = String(value);
      return str.replace(/\s+/g, "").replace(/(\d{5})(\d{5})/, "$1 $2");
    }

    return String(value);
  };

  const toggleReveal = (key) => setRevealed((r) => ({ ...r, [key]: !r[key] }));

  // Render dynamic content from details object
  const renderDetailsGrid = () => {
    if (!project.details) return null;

    const isEmailKey = (k) => /email/i.test(k);
    const entries = Object.entries(project.details).sort(([ka], [kb]) => {
      const aIs = isEmailKey(ka);
      const bIs = isEmailKey(kb);
      if (aIs === bIs) return 0;
      return aIs ? -1 : 1; // email first
    });

    return (
      <Grid container spacing={1.25}>
        {entries.map(([key, rawValue]) => {
          const sensitive = isSensitiveKey(key);
          const shown = revealed[key] || !sensitive;
          const formatted = formatValue(key, rawValue);
          const isEmail = isEmailKey(key);
          const display =
            sensitive && !shown ? String(formatted).replace(/.(?=.{4})/g, "•") : formatted;

          return (
            <Grid item xs={12} md={isEmail ? 12 : 6} key={key}>
              <MDBox py={0.5} pr={1.5} display="flex" alignItems="center" sx={{ minHeight: 32 }}>
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  textTransform="capitalize"
                  sx={{ mr: 0.5 }}
                >
                  {key}:&nbsp;
                </MDTypography>
                <Tooltip title={String(formatted)} placement="top" disableInteractive>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    sx={{
                      overflow: isEmail ? "visible" : "hidden",
                      textOverflow: isEmail ? "unset" : "ellipsis",
                      whiteSpace: isEmail ? "normal" : "nowrap",
                      wordBreak: isEmail ? "break-all" : "normal",
                      flex: 1,
                    }}
                  >
                    {display}
                  </MDTypography>
                </Tooltip>
                {sensitive && (
                  <Tooltip title={shown ? "Hide" : "Show"} placement="top">
                    <IconButton
                      size="medium"
                      onClick={() => toggleReveal(key)}
                      aria-label={shown ? "Hide value" : "Show value"}
                    >
                      {shown ? (
                        <VisibilityOffIcon fontSize="inherit" />
                      ) : (
                        <VisibilityIcon fontSize="inherit" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </MDBox>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "42vh",
          maxHeight: "80vh",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          pt: 1.25,
          px: 2,
          backgroundColor: ({ palette }) => palette.info.main,
          color: ({ palette }) => palette.white.main,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        <MDTypography variant="h6" fontWeight="medium" color="white">
          {project.title}
        </MDTypography>
        <IconButton onClick={onClose} size="medium">
          <CloseIcon sx={{ color: "white" }} />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ m: 0 }} />

      <DialogContent sx={{ pt: 2, px: 3 }}>{renderDetailsGrid()}</DialogContent>

      <Divider />

      <MDBox
        p={2}
        display="flex"
        justifyContent="flex-end"
        sx={{ position: "sticky", bottom: 0, background: "white" }}
      >
        <MDButton variant="gradient" color="info" onClick={onClose}>
          Close
        </MDButton>
      </MDBox>
    </Dialog>
  );
}

ProjectDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  project: PropTypes.shape({
    title: PropTypes.string,
    details: PropTypes.object,
  }),
};

export default ProjectDetailsModal;
